import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '../lib/api';

const ALL_SCOPES = ['tickets:read', 'tickets:write', 'kb:read', 'events:read', 'webhooks:read', 'webhooks:write'];

export default function ApiKeysPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);

  const { data = [] } = useQuery({ queryKey: ['api-keys'], queryFn: async () => (await api.get('/api-keys')).data });

  const revoke = useMutation({
    mutationFn: async (id: string) => api.post(`/api-keys/${id}/revoke`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['api-keys'] }); toast.success('Revoked'); },
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">API Keys</h1>
          <p className="text-sm text-slate-500">For other RunTechByte products and external apps to push tickets in.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ Create key</button>
      </div>

      {issued && (
        <div className="card border-brand-300 bg-brand-50 p-5 mb-4">
          <div className="text-sm font-semibold text-brand-900">New API key — copy it now, you won't see it again</div>
          <div className="mt-2 font-mono text-xs bg-white border border-brand-200 rounded p-2 break-all select-all">{issued}</div>
          <button className="btn-ghost mt-3" onClick={() => setIssued(null)}>Dismiss</button>
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Prefix</th>
              <th className="text-left px-4 py-3">Scopes</th>
              <th className="text-left px-4 py-3">Last used</th>
              <th className="text-left px-4 py-3">Status</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data as { id: string; name: string; prefix: string; scopes: string[]; lastUsedAt?: string; revokedAt?: string }[]).map((k) => (
              <tr key={k.id}>
                <td className="px-4 py-3 font-medium">{k.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{k.prefix}…</td>
                <td className="px-4 py-3 text-xs text-slate-600">{k.scopes.join(', ')}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</td>
                <td className="px-4 py-3">{k.revokedAt ? <span className="badge bg-rose-100 text-rose-700">revoked</span> : <span className="badge bg-emerald-100 text-emerald-700">active</span>}</td>
                <td className="px-4 py-3">{!k.revokedAt && <button className="btn-ghost text-rose-600" onClick={() => revoke.mutate(k.id)}>Revoke</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewKeyModal onClose={() => setShowNew(false)} onCreated={(key: string) => { setIssued(key); setShowNew(false); qc.invalidateQueries({ queryKey: ['api-keys'] }); }} />}
    </div>
  );
}

function NewKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: (key: string) => void }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['tickets:write', 'tickets:read']);
  const create = useMutation({
    mutationFn: async () => (await api.post('/api-keys', { name, scopes })).data,
    onSuccess: (d: { key: string }) => onCreated(d.key),
    onError: (e: { response?: { data?: { error?: string } } }) => toast.error(e.response?.data?.error ?? 'Failed'),
  });
  function toggle(s: string) {
    setScopes((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  }
  return (
    <div className="fixed inset-0 bg-slate-900/40 grid place-items-center p-4 z-50">
      <div className="card w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">Create API key</h2>
        <div className="space-y-3">
          <div><label className="label">Name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. BillX → TTB" /></div>
          <div>
            <label className="label">Scopes</label>
            <div className="space-y-1">
              {ALL_SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={scopes.includes(s)} onChange={() => toggle(s)} /> {s}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!name || scopes.length === 0 || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}
